import {
  VictoryCaptionEditor,
  Leaderboard,
  VsBattlePopup,
  ItemEditorPopup,
  ShopPopup,
  KitchenPopup,
  ArcadeBattlePopup,
  ResultBackground,
  PlayerLabel,
  UnityPlayer,
  MetaversePopup,
  VoiceoverInput,
  VoiceoverChromakey,
  VoiceoverPlayback,
  BroadsiderPromo,
  BroadsiderPromoOption,
  BroadsiderMenu,
  FigureViewDoubleCanvas,
  BroadsiderInventory,
  BroadsiderStats,
  BroadsiderInventorySelector,
  BroadsiderFilters,
  BroadsiderAssets,
  GreenScreen,
  IconButton,
  FirstBattle
} from '../../components';
import { FigureModel } from '../../models';
import { StyledNExtBattleButtonHolder, StyledBroadsiderHud, StyledMatchListControls } from './BroadsiderHud.styled';
import { theme } from '../../theme';
import { downloadFile, convertMsToDuration, openUrl, convertUrl } from '../../utils';
import { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { useWebSocket, useTokenAuth, useLevels } from '../../hooks';
import { AppContext } from '../../providers/AppContext';
import { DiscordSdkContext } from '../../providers/DiscordSdkProvider';
import { textToSpeech, getVoicesList } from '../../providers/TextToSpeech';
import { INSTAGRAM_FILTER_URL, DISCORD_SERVER_INVITE_URL, VS_DISABLED } from '../../constants';
import { playLevel, leaveGameSession, updateVictoryCaption, setTutorialState, getGameSession } from '../../providers/BasejumpApiProvider';
import { AssetModel } from '../../models';
import useAppEvent from '../../hooks/useAppEvent';
import { useTutorialStore, TutorialEnum } from '../../store/tutorialModal.store';
import { useEarningsStore } from '../../store/gameEarnings.store';

const SESSION_POLLING_INTEVAL_MS = 30000;

const NextBattleButtonHolder = ({ onGameStarted }) => {
  const { levels } = useLevels();
  const { selectedBroadsider } = useContext(AppContext);
  const { basejumpToken } = useTokenAuth();

  const onNextBattle = async () => {
    const sortedLevels = levels.filter(l => !l.locked).sort((a, b) => b.order - a.order);
    const level = sortedLevels[0];
    const session = await playLevel(level.id, selectedBroadsider.getAvatarId(), basejumpToken);
    onGameStarted(session);
  };

  return (
    <StyledNExtBattleButtonHolder>
      <IconButton onClick={onNextBattle} responsive={true}>
        Battle
      </IconButton>
    </StyledNExtBattleButtonHolder>
  );
};

const MatchListControls = ({ showLeaderboard, setShowLeaderboard, user, onSaveVictoryCaption }) => {
  return (
    <StyledMatchListControls>
      <VictoryCaptionEditor initText={user.victory_caption || ''} onSave={onSaveVictoryCaption}/>
      <IconButton onClick={() => setShowLeaderboard(!showLeaderboard)} responsive={true}>
        {showLeaderboard ? 'To Battles' : 'Leaderboard'}
      </IconButton>
    </StyledMatchListControls>
  );
};

const MetaverseLogo = () => {
  return (
    <div className='hud-metaverse-logo'>
      <div className='hud-metaverse-logo-border' />
      <img src='/assets/low-respng.png' />
    </div>
  );
};

const VOICE_FILE_NAME = 'voice.mp3';

const parseEquipment = (equipment, figure, forOpponent = false) => {
  if (!figure || !equipment) return {};
  const items = equipment.map(e => AssetModel.fromBasejumpEquipment(e));
  const weapons = items.filter(w => w.getType() === 'weapon').map((w) => ({ weaponType: w.nftData.subtype, weaponGlbPath: `${w.nftData.glb}?opponent=${!!forOpponent}`, effects: w.getEffects() }));
  const equippedObject = items.find(w => w.getType() === 'object');
  let objectPath = (equippedObject && ['shield', 'equip', 'food'].includes(equippedObject.nftData.subtype)) ? equippedObject.nftData.glb : null;
  const objectType = objectPath ? equippedObject.nftData.subtype : null;
  let dronePath = (equippedObject && (equippedObject.nftData.subtype || '').startsWith('drone')) ? equippedObject.nftData.glb : null;
  const droneType = (dronePath && equippedObject.nftData.subtype === 'drone_dance')
    ? 'dance'
    : (dronePath && equippedObject.nftData.subtype === 'drone_mutatio')
      ? 'mutatio'
      : null;
  let companionPath = (equippedObject && (equippedObject.nftData.subtype === 'companion')) ? equippedObject.nftData.glb : null;

  const armorObject = items.find(w => w.getType() === 'armor');
  let armorPath = null;
  if (armorObject) {
    armorPath = armorObject.nftData.glb;
    if (figure.isHenchie() && armorObject.nftData.henchie_glb) {
      armorPath = armorObject.nftData.henchie_glb;
    }
  }
  if (forOpponent) {
    if (dronePath) {
      dronePath = `${dronePath}?opponent=true`;
    }
    if (objectPath) {
      objectPath = `${objectPath}?opponent=true`;
    }
    if (armorPath) {
      armorPath = `${armorPath}?opponent=true`;
    }
  }
  return {
    droneType,
    dronePath,
    objectType,
    objectPath,
    companionPath,
    weapons,
    armorPath
  }
};

const BroadsiderHud = ({ figure, metaverseData = [], inventory = { slots: [] } }) => {
  const { isDiscord, invite, joinServer } = useContext(DiscordSdkContext);
  const [playbackDuration, setPlaybackDuration] = useState('-- : --');
  const [voiceGenInProgress, setVoiceGenInProgress] = useState(false);
  const [showGreenScreen, setShowGreenScreen] = useState(false);
  const [lipsync, setLipsync] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [voiceIndex, setVoiceIndex] = useState(0);
  const [lastGeneratedText, setLastGeneratedText] = useState('');
  const [lastGeneratedVoice, setLastGeneratedVoice] = useState('');
  const [inventoryVisible, setInventoryVisible] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const [selectedInventorySlotIndex, setSelectedInventorySlotIndex] = useState(-1);
  const [showMetaverse, setShowMetaverse] = useState(false);
  const [showVoiceover, setShowVoiceover] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showShop, _setShowShop] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showVsBattle, setShowVsBattle] = useState(false);
  const [showArcadeBattle, setShowArcadeBattle] = useState(false);
  const [showFirstBattle, setShowFirstBattle] = useState(false);
  const [queueData, setQueueData] = useState(null);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [showKitchen, _setShowKitchen] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isGamePaused, setIsGamePaused] = useState(false);
  const showTutorial = useTutorialStore(state => state.showTutorial);

  const { levels } = useLevels();
  const { subscribeToAppEvent, unsubscribeFromAppEvent } = useAppEvent('modalAction');

  const selectedMenuRef = useRef(0);

  const {
    gameSessionData,
    setGameSessionData,
    ownedBroadsiders,
    showKitchen: requestedKitchen,
    setShowKitchen: setRequestedKitchen,
    showShop: requestedShop,
    setShowShop: setRequestedShop,
    setQrCodeData,
    setErrorData,
    voices: availableVoices,
    setVoices,
    voiceoverApiKey,
    setVoiceoverApiKey,
    avatarStats,
    avatarStatsModifiers,
    readySessionId,
    setReadySessionId,
    selectedBroadsider,
    homepageSelectedAvatarId
  } = useContext(AppContext);
  const [voiceoverEditMode, setVoiceoverEditMode] = useState(!voiceoverApiKey);
  const controls = {};
  const weapons = inventory.slots.filter((slot) => slot.equipped && slot.equipped.getType() === 'weapon').map((s, i) => ({ weaponType: s.equipped.getSubType(), weaponGlbPath: `${s.equipped.getGlbSource()}?index=${i}`, effects: s.equipped.getEffects() }));
  const equippedObject = inventory.slots.find((slot) => slot.equipped && slot.equipped.getType() === 'object')?.equipped;
  const objectPath = (equippedObject && ['shield', 'equip', 'food'].includes(equippedObject.nftData.subtype)) ? equippedObject.nftData.glb : null;
  const objectType = objectPath ? equippedObject.nftData.subtype : null;
  const dronePath = (equippedObject && (equippedObject.nftData.subtype || '').startsWith('drone')) ? equippedObject.nftData.glb : null;
  const droneType = (dronePath && equippedObject.nftData.subtype === 'drone_dance')
    ? 'dance'
    : (dronePath && equippedObject.nftData.subtype === 'drone_mutatio')
      ? 'mutatio'
      : null;
  const companionPath = (equippedObject && (equippedObject.nftData.subtype === 'companion')) ? equippedObject.nftData.glb : null;
  const { isConnected: wsIsConnected, lastMessage } = useWebSocket();
  const { basejumpToken, user, setUser } = useTokenAuth();

  const {
    showAcceptModal,
    gameSessionResult,
    setGameResult,
    lastPlayedLevel,
    battleNowModalScheduled,
    displayBattleNowModal,
    scheduleBattleNowModal,
    cancelBattleNowModal
  } = useEarningsStore();

  const setSelectedMenuIndex = (menuIndex) => {
    selectedMenuRef.current = menuIndex
  };

  const setShowKitchen = (value) => {
    _setShowKitchen(value);
    if (!value) {
      setRequestedKitchen(value);
    }
  }
  
  const setShowShop = (value) => {
    _setShowShop(value);
    if (!value) {
      setRequestedShop(value);
    }
  }

  const nextLockedLevel = useMemo(() => {
    if (!levels.length) {
      return -1;
    }
    const sortedLevels = levels.filter(l => l.locked).sort((a, b) => a.order - b.order);
    if (!sortedLevels.length) {
      return -1;
    }
    return sortedLevels[0].order;
  }, [levels]);
  
  
  
  const goToNextBattle = async () => {
    const sortedLevels = levels.filter(l => !l.locked).sort((a, b) => b.order - a.order);
    const level = sortedLevels[0];
    const session = await playLevel(level.id, selectedBroadsider.getAvatarId(), basejumpToken);
    selectArcadeBattle();
    onSinglePlayerStart(session);
  }

  useEffect(() => {
    if (requestedKitchen) {
      selectKitchen();
      setSelectedMenuIndex(menuOptions.findIndex(e => e.title === 'Bubble Kitchen'));
    }
  }, [requestedKitchen]);
  
  useEffect(() => {
    if (requestedShop) {
      selectShop();
      setSelectedMenuIndex(menuOptions.findIndex(e => e.title === 'Shop'));
    }
  }, [requestedShop]);

  useEffect(() => {
    if (showKitchen) {
      if (controls.moveAvatarRight) {
        controls.moveAvatarRight();
      }
    }
  }, [showKitchen]);

  useEffect(() => {
    if (gameSessionData && basejumpToken) {
      const interval = setInterval(async () => {
        console.info(`Polling session state ${gameSessionData.id}`)
        const session = await getGameSession(gameSessionData.id, basejumpToken)
        console.info(`Session state ${gameSessionData.id}: ${session.session_state}`);
        switch (session.session_state) {
          case 'in_progress':
            setReadySessionId(session.id);
            break;
          case 'expired':
          case 'error':
            setErrorData({ title: 'Game session failed', description: 'Your game session failed. Please try again' });
            setGameSessionData(null);
            setReadySessionId(null);
            break;
          case 'finished':
            setGameSessionData(null);
            setReadySessionId(null);
            break;
          default:
            break;
        }
      }, SESSION_POLLING_INTEVAL_MS)
      return () => clearInterval(interval);
    }
  }, [gameSessionData, basejumpToken]);

  useEffect(() => {
    if (lastMessage) {
      switch (lastMessage.type) {
        case 'session_ready':
          console.error(`Staring game session ${lastMessage.data.id}`);
          if (lastMessage.data?.id) {
            // If my session is ready, start it anyways
            if (!gameSessionData) {
              setGameSessionData(lastMessage.data);
            }
            setReadySessionId(lastMessage.data.id);
          }
          break;
        case 'session_created':
          console.error(`Created game session ${lastMessage.data.id}`);
          if (lastMessage.data?.id) {
            setGameSessionData(lastMessage.data);
          }
          break;
        case 'session_failed':
          if (gameSessionData && lastMessage.data?.id && gameSessionData.id === lastMessage.data?.id) {
            setGameSessionData(null);
            setReadySessionId(null);
            setErrorData({ title: 'Game session failed', description: 'Your game session failed. Please try again' });
          }
          break;
        case 'queue_notification':
          if (gameSessionData && lastMessage.data?.session && gameSessionData.id === lastMessage.data?.session) {
            setQueueData(lastMessage.data);
          }
          break;
        case 'error':
          setErrorData({ title: 'Server Error', description: lastMessage.data?.message });
          break;
        case 'message':
          setErrorData({ title: 'Message', description: lastMessage.data?.message });
          break;
        default:
          console.error(JSON.stringify(lastMessage));
          break;
      }
    }
  }, [lastMessage]);


  /**
   * Listen for modalAction events
   */
  useEffect(() => {
    const callback = ({ type, data }) => {
      // Navigate to menu item on request from modal
      if (type === 'navigate') {
        navigateByIndex(data)
      } else if (type === 'pause') {
        setIsGamePaused(data ? true : false);
      } else if (type === 'battle') {
        goToNextBattle();
      } else if (type === 'item_consumed') {
        if (battleNowModalScheduled) {
          displayBattleNowModal();
        }
      } else if (type === 'postBattleDone') {
        if (lastPlayedLevel < 3 && nextLockedLevel - lastPlayedLevel === 1) {
          scheduleBattleNowModal(nextLockedLevel);
        }
      }
    }
    subscribeToAppEvent(callback);

    return () => unsubscribeFromAppEvent(callback);
  }, [battleNowModalScheduled, nextLockedLevel, lastPlayedLevel])

  // Dispay battle now modal
  useEffect(() => {
    // 15 seconds delay 
    const timeForBattleNowIdle = 15000;
    let timeout;
  
    if (battleNowModalScheduled) {
      timeout = setTimeout(() => {
        displayBattleNowModal();
      }, timeForBattleNowIdle)
    }

    return () => {
      if (battleNowModalScheduled) {
        clearTimeout(timeout);
      }
    }

  }, [battleNowModalScheduled])

  // Check If FirstBattle view was not displayed before
  const firstBattleDisplayed = user && user.tutorial_state?.first_battle_displayed;
  useEffect(() => {
    if (user && !firstBattleDisplayed) {
      if (homepageSelectedAvatarId) {
        onFirstBattleClicked();
      } else {
        showFirstBattleView();
      }
    }
  }, [firstBattleDisplayed, !!user, homepageSelectedAvatarId]);

  const armorObject = inventory.slots.find((slot) => slot.equipped && slot.equipped.getType() === 'armor')?.equipped;
  let armorPath = null;
  if (armorObject) {
    armorPath = armorObject.nftData.glb;
    if (figure.isHenchie() && armorObject.nftData.henchie_glb) {
      armorPath = armorObject.nftData.henchie_glb;
    }
  }

  const showFirstBattleView = () => {
    setIsMenuVisible(false);
    setShowFirstBattle(true);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    controls.centerAvatar();
  };
  const selectEquip = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(true);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    if (selectedInventorySlotIndex >= 0) {
      controls.moveAvatarLeft();
    } else {
      controls.centerAvatar();
    }
  };
  const selectMetaverse = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(true);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    controls.moveAvatarRight();
  };
  const selectKitchen = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(true);
    controls.moveAvatarRight();
  };
  const selectShop = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(true);
    setShowKitchen(false);
    controls.moveAvatarRight();
  };
  const selectVsBattle = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(true);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    setShowLeaderboard(false);
    controls.moveToChallenge();
  };
  const selectArcadeBattle = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(true);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    setShowLeaderboard(false);
    controls.moveToChallenge();
  };
  const selectFilters = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(true);
    setShowArcadeBattle(false);
    setShowVsBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    controls.centerAvatar();
  };
  const selectVoice = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(true);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowEditor(false);
    setShowShop(false);
    setShowKitchen(false);
    controls.zoomToFace();
  };

  const selectEditor = () => {
    setIsMenuVisible(true);
    setShowFirstBattle(false);
    setSelectedOpponent(null);
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowFilters(false);
    setShowVsBattle(false);
    setShowArcadeBattle(false);
    setShowShop(false);
    setShowEditor(true);
    setShowKitchen(false);
  };

  const promoOptions = useMemo(() => {
    const options = [
      { image: '/assets/follow_drone.png', text: 'INVITE FRIENDS, GET DRONES: INCREASES HEALTH, ATTACK AND DEFENSE POINTS', onClick: () => 
        isDiscord ? invite() : setErrorData({ title: 'Not Available', description: 'Please join our Discord App to invite friends!' })
      },
      { image: '/assets/burger.png', text: 'JOIN THE OFFICIAL BASEJUMP DISCORD SERVER AND UNLOCK A FREE BURGER HEALTH BOOST', onClick: () =>
        isDiscord ? joinServer() : openUrl(DISCORD_SERVER_INVITE_URL)
      },
      { image: '/assets/peru.png', text: 'SHOP PURCHASES HELP PROTECT OVER 4MM ACRES OF REAL WORLD RAINFOREST', onClick: () =>
        selectOption(menuOptions.find(o => o.title === 'Shop'))
      }
    ];
    return options;
  }, [isDiscord]); 

  const onSelectShop = () => {
    selectOption(menuOptions.find(o => o.title === 'Shop'))
  };

  const generateAudio = async (text) => {
    setVoiceGenInProgress(true);
    try {
      const url = await textToSpeech(text, availableVoices[voiceIndex].id, voiceoverApiKey);
      return url;
    } catch (e) {
      setErrorData({ title: 'Voice API Error', description: e.message });
    } finally {
      setVoiceGenInProgress(false);
    }
  };

  const saySomething = (url) => {
    if (url) {
      controls.speak(url);
    }
  }

  const onGenerateVoiceClicked = async (text) => {
    const url = await generateAudio(text);
    if (url) {
      setLastGeneratedText(text);
      setLastGeneratedVoice(availableVoices[voiceIndex].id);
      setAudioUrl(url);
      saySomething(url);
    }
  }

  const menuOptions = useMemo(() => {
    const menuOptions = [
      { color: theme.baseRed, title: 'Equip', logo: '/assets/equip.svg', logoHover: '/assets/equip_hover.svg', descriptions: ['アクションフィギュアを装備する','액션 피규어를 준비하세요','装备你的人偶'], onSelect: selectEquip },
      { color: theme.baseBlue, title: 'Broadside Battle', logo: '/assets/menu_skull.svg', logoHover: '/assets/menu_skull_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectArcadeBattle },
      { color: theme.baseBlue, title: 'Vs. Battle', logo: '/assets/vs_battle.svg', logoHover: '/assets/vs_battle_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectVsBattle, disabled: VS_DISABLED },
      { color: theme.selectionGreen, title: 'Bubble Kitchen', logo: '/assets/bubblekitchen.svg', logoHover: '/assets/bubblekitchen_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectKitchen },
      { color: theme.baseOrange, title: 'Shop', logo: '/assets/shop.svg', logoHover: '/assets/shop_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectShop }
    ];
    if (!isDiscord) {
      menuOptions.push(
       { color: theme.baseYellow, title: 'Explore', logo: '/assets/metaverse.svg', logoHover: '/assets/metaverse_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectMetaverse },
       { color: theme.baseGreen, title: 'Create', logo: '/assets/voiceover.svg', logoHover: '/assets/voiceover_hover.svg', descriptions: ['テキスト読み上げ','텍스트를 음성으로','文字转语音'], onSelect: selectVoice },
       { color: theme.baseViolet, title: 'Files & Filters', logo: '/assets/traits.svg', logoHover: '/assets/traits_hover.svg', descriptions: ['ファイルとフィルター','파일 및 필터','文件和过滤器'], onSelect: selectFilters }
      );
      if (user && user.has_editor) {
        menuOptions.push(
          { color: theme.baseViolet, title: 'Editor & Metrics', logo: '/assets/editor.svg', logoHover: '/assets/editor_hover.svg', descriptions: ['ファイルとフィルター','파일 및 필터','文件和过滤器'], onSelect: selectEditor }
        );
      }
    }
    return menuOptions;
  }, [user]);

  const selectOption = (option) => {
    const newIndex = menuOptions.findIndex((e) => e.title == option.title);
    if (newIndex === selectedMenuRef.current) {
      return;
    }
    if (option.onSelect) {
      option.onSelect();
    }
    setSelectedMenuIndex(newIndex);
  }

  const navigateByIndex = (menuIndex) => {
    if (menuIndex === selectedMenuRef.current) {
      return;
    }
    const option = menuOptions[menuIndex];
    if (option.onSelect) {
      option.onSelect();
    }
    setSelectedMenuIndex(menuIndex);
  }

  const handleAvatavi = () => {
    setQrCodeData({ url: convertUrl(figure.getVrmSource()), label: 'Scan\nwith Your\nAvatavi App' });
  }

  const handleAr = () => {
    const viewerUrl = convertUrl(`/viewer/${encodeURIComponent(convertUrl(figure.getGlbSource()))}`);
    setQrCodeData({ url: viewerUrl, label: 'Scan\nwith Your\nSmartphone' });
  }

  const handleTt = () => {
    setErrorData({ title: 'Tiktok Error', description: 'Tiktok filters are not available at the moment' });
  }

  const handleInsta = () => {
    setQrCodeData({ url: INSTAGRAM_FILTER_URL, label: 'Scan\nwith Your\nSmartphone' });
  }

  const handleNewVoiceoverApiKey = async (key) => {
    try {
      setVoicesLoading(true);
      const voicesFromApi = await getVoicesList(key);
      setVoices(voicesFromApi);
      setVoiceoverApiKey(key);
      setVoiceoverEditMode(false);
    } catch (e) {
      setErrorData({ title: 'Voice API Error', description: e.message });
    } finally {
      setVoicesLoading(false);
    }
  };

  const onInventorySlotSelected = (index) => {
    setSelectedInventorySlotIndex(index);
    if (index >= 0) {
      controls.moveAvatarLeft();
    } else {
      controls.centerAvatar();
    }
  };

  const onWin = ({ level }) => {
    setGameSessionData(null);
    setReadySessionId(null);
    setGameResult({ status: 'Won', level: level})
  };

  const onLose = ({ level }) => {
    setGameSessionData(null);
    setReadySessionId(null);
    setGameResult({ status: 'Lost', level: level });
  };

  const onLevelStarted = ({ level }) => {
    const levelsTutorials = {
      0: TutorialEnum.Basics,
      1: TutorialEnum.Dash,
      2: TutorialEnum.Combos,
      3: TutorialEnum.CombosAdvanced,
      4: TutorialEnum.RangeWeapon,
    };

    const existingTutorialSettings = user?.tutorial_state || {};
    const viewedTutorials = existingTutorialSettings?.level_turorials_viewed;
    const levelIsViewed = viewedTutorials?.[level] === true;

    if (typeof levelsTutorials[level] !== 'undefined' && !levelIsViewed) {
      const tutorialId = levelsTutorials[level];
      showTutorial(tutorialId);
      setLevelTutorialViewed(level);
    }
  }

  const setLevelTutorialViewed = async (level) => {
    try {
      const existingTutorialSettings = user?.tutorial_state || {};
      const viewedTutorials = existingTutorialSettings.level_turorials_viewed || {};

      const updatedUser = await setTutorialState(basejumpToken, {
        ...existingTutorialSettings,
        level_turorials_viewed: {
          ...viewedTutorials,
          [level]: true
        }
      });
      setUser(updatedUser);
    } catch (e) { 
      console.error('Failed to update user tutorial state');
    }
  }

  const onSelectOpponent = (opponentAvatar) => {
    controls.moveToChallenge();
    setSelectedOpponent(opponentAvatar);
  };

  const onCancelSession = async () => {
    await leaveGameSession(gameSessionData.id, basejumpToken);
    setGameSessionData(null);
  };

  const onSinglePlayerStart = (session) => {
    cancelBattleNowModal();
    setGameSessionData(session);
  };

  const onSaveVictoryCaption = async (caption) => {
    user.victory_caption = caption;
    await updateVictoryCaption(caption, basejumpToken);
  };

  const onFirstBattleClicked = async () => {
    goToNextBattle();

    try {
      const existingTutorialSettings = user?.tutorial_state || {};
      const updatedUser = await setTutorialState(basejumpToken, {
        ...existingTutorialSettings,
        first_battle_displayed: true
      });
      setUser(updatedUser);
    } catch (e) { 
      console.error('Failed to update user tutorial state');
    }
  }
  

  return (
    <StyledBroadsiderHud>
      <div className='scroller'>
        <FigureViewDoubleCanvas
          isDrunk={(avatarStatsModifiers?.healthMultiplier || 1.0) <= 0.7}
          figure={figure}
          opponent={(!showAcceptModal && selectedOpponent) ? FigureModel.fromBasejumpAvatar(selectedOpponent, isDiscord) : null}
          opponentEquipment={parseEquipment(selectedOpponent?.equipment, selectedOpponent ? FigureModel.fromBasejumpAvatar(selectedOpponent, isDiscord): null, true)}
          objectPath={objectPath}
          weapons={weapons}
          armorPath={armorPath}
          dronePath={dronePath}
          companionPath={companionPath}
          objectType={objectType}
          droneType={droneType}
          width='100%'
          height='100%'
          controls={controls}
          onLipsyncCreated={(ls) => setLipsync(ls)}
          onPlaybackStart={(durationMs) => {
            setPlaying(true);
            setPlaybackDuration(convertMsToDuration(durationMs));
          }}
          onPlaybackStop={() => setPlaying(false)}
        />
        {isMenuVisible &&<div className='hud-panel hud-panel-left hud-panel-top'>
          <BroadsiderMenu options={menuOptions} onSelect={(option) => selectOption(option)} selectedIndex={selectedMenuRef.current} />
        </div>}
        <div className='player-label player-label-you' style={{ opacity: (showVsBattle || showArcadeBattle) && !gameSessionResult ? '1': '0' }}>
          <PlayerLabel name='You' xp={user?.xp || 0} />
        </div>
        <div className='player-label player-label-opponent' style={{ opacity: (showVsBattle || showArcadeBattle) && !gameSessionResult && selectedOpponent ? '1': '0' }}>
          <PlayerLabel you={false} name={selectedOpponent?.owner.user_name} xp={selectedOpponent?.owner?.xp || 0} isNpc={selectedOpponent?.owner.isNpc}/>
        </div>
      </div>
      
      {showFirstBattle && <div className='hud-panel hud-panel-dynamic-width hud-panel-left hud-panel-top hud-panel-bottom'>
        <FirstBattle onBattleStart={onFirstBattleClicked} />
      </div>}
      {inventoryVisible && <div className='hud-panel hud-panel-right hud-panel-top hud-panel-full-height no-blur'>
        <div className='hud-panel-content'>
          <BroadsiderInventory inventory={inventory} selectedSlotIndex={selectedInventorySlotIndex} onSlotSelected={onInventorySlotSelected} onSelectShop={onSelectShop}/>
          <BroadsiderPromo period={5000}>
            {promoOptions.map((o, index) => (<BroadsiderPromoOption key={index} bigText={o.bigText} image={o.image} text={o.text} onClick={o.onClick}/>))}
            </BroadsiderPromo>
        </div>
      </div>}
      {inventoryVisible && <NextBattleButtonHolder onGameStarted={(session) => { selectArcadeBattle(); onSinglePlayerStart(session);}}/>}
      {inventoryVisible && selectedInventorySlotIndex >= 0 && <div className='hud-panel hud-panel-right-additional hud-panel-top no-blur'>
        <div className='hud-panel-content'>
          <BroadsiderInventorySelector slot={inventory.slots[selectedInventorySlotIndex]}/>
        </div>
      </div>}
      {(inventoryVisible || showKitchen) && <div className='hud-panel hud-panel-left hud-panel-bottom hud-panel-move-back'>
        <BroadsiderStats stats={avatarStats} modifiers={avatarStatsModifiers}/>
      </div>}
      {(showArcadeBattle || showVsBattle) && !gameSessionResult  && <div className='hud-panel hud-panel-left hud-panel-bottom'>
        <MatchListControls user={user} onSaveVictoryCaption={onSaveVictoryCaption} showLeaderboard={showLeaderboard} setShowLeaderboard={(show) => { setSelectedOpponent(null); setShowLeaderboard(show); }} />
      </div>}
      <div className='hud-frame' />
      <div className='hud-frame-bottom' />
      {showMetaverse && <MetaversePopup content={metaverseData}/>}
      {showVsBattle && !gameSessionResult && !showLeaderboard && <VsBattlePopup onSelectOpponent={onSelectOpponent} disabled={!!gameSessionData}/>}
      {(showVsBattle || showArcadeBattle) && showLeaderboard && !gameSessionResult && <Leaderboard onSelectOpponent={onSelectOpponent} />}
      {showArcadeBattle && !gameSessionResult && !showLeaderboard && <ArcadeBattlePopup onSelectOpponent={onSelectOpponent} onGameStarted={onSinglePlayerStart} disabled={!!gameSessionData}/>}
      {showAcceptModal && gameSessionResult && <ResultBackground result={gameSessionResult.status}/>}
      {gameSessionData && <UnityPlayer
        queueData={(queueData && queueData.session === gameSessionData?.id) ? queueData : null}
        onTimeout={onCancelSession}
        onCancelSession={onCancelSession}
        onWin={onWin}
        onLose={onLose}
        onGameStarted={onLevelStarted}
        isGamePaused={isGamePaused}
        gameSessionId={gameSessionData.id}
        sessionData={gameSessionData}
        sessionReady={gameSessionData.id === readySessionId}
        myPortrait={figure.getAvatarImgSource()}
      />}
      {showVoiceover && <VoiceoverInput
        onVoiceSelected={(voice) => setVoiceIndex(availableVoices.findIndex((v) => v.id === voice.id))}
        voiceoverApiKey={voiceoverApiKey}
        onSetApiKey={handleNewVoiceoverApiKey}
        voiceOptions={availableVoices}
        selectedVoiceIndex={voiceIndex}
        lastGeneratedText={lastGeneratedText}
        lastGeneratedVoice={lastGeneratedVoice}
        apiKeyInProgress={voicesLoading}
        editMode={voiceoverEditMode}
        onEditClick={() => setVoiceoverEditMode(true)}
        generationInProgress={voiceGenInProgress}
        onGenerateClicked={(text) => onGenerateVoiceClicked(text)}/>}
      {showVoiceover && <VoiceoverChromakey
        showGreenScreen={showGreenScreen}
        onToggleGreenScreen={() => setShowGreenScreen(!showGreenScreen)}/>}
      {showVoiceover && <VoiceoverPlayback
        disabled={!audioUrl}
        duration={playbackDuration}
        isPlaying={isPlaying}
        lipsync={lipsync}
        onClickPlay={() => isPlaying ? controls.stopTalking() : saySomething(audioUrl)}
        onClickDownload={() => downloadFile(audioUrl, VOICE_FILE_NAME)}/>}
      {showGreenScreen && <GreenScreen />}
      {showFilters && <div className='hud-panel hud-panel-right hud-panel-top hud-panel-full-height no-blur'>
        <div className='hud-panel-content'>
          <BroadsiderFilters figure={figure} onAvatavi={handleAvatavi} onAr={handleAr} onTt={handleTt} onInsta={handleInsta}/>
          <BroadsiderAssets figure={figure} />
        </div>
      </div>}
      {showEditor && <ItemEditorPopup />}
      {showShop && <ShopPopup />}
      {showKitchen && <KitchenPopup onGameStarted={(session) => { selectArcadeBattle(); onSinglePlayerStart(session);}}/>}
    </StyledBroadsiderHud>
  );
};

export default BroadsiderHud;
